import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api } from '../api/client'
import { dynamicFieldsApi, type DynamicFieldDefinition } from '../api/dynamicFields'
import { fetchPipelines, type Pipeline } from '../api/pipelines'
import { useFeatureFlag } from '../lib/featureFlags'
import {
  Button, ButtonLink, Callout, Card, CardContent, CardHeader, DataTable, LoadingState, Page, PageHeader, Select, StatusPill, Tabs,
} from '../components/ui'

/**
 * R3-12 (REQ-043-003, REQ-041-001): scheda del prodotto con il tab "Configurazione": i campi della pratica con ambito
 * proprio su questo prodotto (obbligatori per primi) e la pipeline associata. Dietro il flag
 * `pratiche.campi_condizionali`: spento, il server risponde 404 e la pagina dice che la funzione non e' attiva.
 * I campi si definiscono in Impostazioni > Campi della pratica (scegliendo il prodotto nell'ambito).
 */
interface ProductHead {
  id: number
  name: string
  code: string | null
  isActive: boolean
  pipelineId: number | null
}

const TYPE_LABEL: Record<string, string> = { Casella: 'Casella', ElencoMultiplo: 'Elenco multiplo', CodiceFiscale: 'Codice fiscale', PartitaIva: 'Partita IVA' }

export function ProductDetailPage() {
  const { id } = useParams()
  const productId = Number(id)
  const enabled = useFeatureFlag('pratiche.campi_condizionali')
  const [tab, setTab] = useState<'scheda' | 'configurazione'>('configurazione')
  const [product, setProduct] = useState<ProductHead | null>(null)
  const [fields, setFields] = useState<DynamicFieldDefinition[]>([])
  const [pipelines, setPipelines] = useState<Pipeline[]>([])
  const [pipelineId, setPipelineId] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const head = (await api<{ product: ProductHead }>(`/api/products/${productId}`)).product
      setProduct(head)
      setPipelineId(head.pipelineId === null || head.pipelineId === undefined ? '' : String(head.pipelineId))
      if (enabled) {
        const [list, pipes] = await Promise.all([dynamicFieldsApi.listForProduct(productId), fetchPipelines().catch(() => [] as Pipeline[])])
        setFields(list)
        setPipelines(pipes)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore di caricamento')
    } finally {
      setLoading(false)
    }
  }, [productId, enabled])

  useEffect(() => {
    if (Number.isFinite(productId)) void load()
  }, [load, productId])

  async function savePipeline() {
    setSaving(true)
    setMessage(null)
    try {
      await api(`/api/products/${productId}/configuration`, {
        method: 'PUT',
        body: JSON.stringify({ pipelineId: pipelineId === '' ? null : Number(pipelineId) }),
      })
      setMessage({ tone: 'success', text: pipelineId === '' ? 'Pipeline tolta dal prodotto.' : 'Pipeline associata al prodotto.' })
      await load()
    } catch (e) {
      setMessage({ tone: 'error', text: e instanceof Error ? e.message : 'Errore nel salvataggio' })
    } finally {
      setSaving(false)
    }
  }

  const required = fields.filter(f => f.required)
  const savedPipeline = product?.pipelineId == null ? '' : String(product.pipelineId)

  return (
    <Page>
      <PageHeader
        title={
          <>
            {product?.name ?? `Prodotto #${id}`}{' '}
            {product && (product.isActive ? <StatusPill tone="success">Attivo</StatusPill> : <StatusPill tone="neutral">Inattivo</StatusPill>)}
          </>
        }
        icon="inventory_2"
        back={{ to: '/products', label: 'Prodotti' }}
        actions={<ButtonLink to={`/products/${id}/governance`} variant="text" size="sm" icon="account_tree">Dove è usato</ButtonLink>}
      />

      {loading && <LoadingState />}
      {error && <Callout tone="error">{error}</Callout>}

      {!loading && !error && product && (
        <>
          <Tabs
            aria-label="Sezioni del prodotto"
            items={[{ id: 'scheda', label: 'Scheda' }, { id: 'configurazione', label: 'Configurazione' }]}
            value={tab}
            onChange={t => setTab(t === 'scheda' ? 'scheda' : 'configurazione')}
          />

          {tab === 'scheda' && (
            <Card variant="outlined" className="mt-3">
              <CardContent>
                <div>Codice: {product.code ?? '—'}</div>
                <div className="text-muted md-typescale-body-small">I dati commerciali si modificano dall&apos;elenco Prodotti.</div>
              </CardContent>
            </Card>
          )}

          {tab === 'configurazione' && !enabled && (
            <Callout tone="info">La configurazione per prodotto non è attiva.</Callout>
          )}

          {tab === 'configurazione' && enabled && (
            <>
              <Card variant="outlined" className="mt-3" data-testid="product-required-fields">
                <CardHeader title={`Campi della pratica per questo prodotto (${required.length} obbligatori)`} />
                <CardContent>
                  {fields.length === 0 ? (
                    <div className="text-muted md-typescale-body-small">
                      Nessun campo ha questo prodotto come ambito. Si definiscono in{' '}
                      <Link to="/settings/dynamic-fields">Impostazioni, Campi della pratica</Link>.
                    </div>
                  ) : (
                    <DataTable variant="plain" caption="Campi della pratica con ambito sul prodotto">
                      <thead>
                        <tr><th scope="col">Campo</th><th scope="col">Tipo</th><th scope="col">Sezione</th><th scope="col">Obbligo</th></tr>
                      </thead>
                      <tbody>
                        {fields.map(f => (
                          <tr key={f.id}>
                            <td>{f.label} <span className="text-muted">({f.code})</span></td>
                            <td>{TYPE_LABEL[f.type] ?? f.type}</td>
                            <td>{f.section ?? '—'}</td>
                            <td>{f.required ? <StatusPill tone="warning">Obbligatorio</StatusPill> : <StatusPill tone="neutral">Facoltativo</StatusPill>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </DataTable>
                  )}
                </CardContent>
              </Card>

              <Card variant="outlined" className="mt-3" data-testid="product-pipeline">
                <CardHeader title="Pipeline associata" />
                <CardContent>
                  {message && <Callout tone={message.tone}>{message.text}</Callout>}
                  <Select id="prd-pipeline" label="Pipeline" value={pipelineId} onChange={e => setPipelineId(e.target.value)}>
                    <option value="">Nessuna</option>
                    {pipelines.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    {pipelineId !== '' && !pipelines.some(p => String(p.id) === pipelineId) && <option value={pipelineId}>{`Pipeline #${pipelineId}`}</option>}
                  </Select>
                  <div className="mt-2">
                    <Button type="button" variant="tonal" size="sm" disabled={saving || pipelineId === savedPipeline} onClick={() => void savePipeline()}>
                      {saving ? 'Salvataggio...' : 'Salva'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </>
      )}
    </Page>
  )
}
